import React from 'react';
import { motion } from 'framer-motion';
import { 
  GraduationCap, Heart, Target, Users, Award, 
  Lightbulb, BookOpen, Rocket
} from 'lucide-react';
import { useQuery } from 'react-query';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import { cmsAPI, teamAPI } from '../services/api';

const AboutPage: React.FC = () => {
  // Fetch site settings
  const { data: settings } = useQuery('siteSettings', async () => {
    const response = await cmsAPI.getSettings();
    return response.data;
  });

  // Fetch features from database
  const { data: features = [] } = useQuery('features', async () => {
    const response = await cmsAPI.getFeatures();
    return response.data?.results || response.data || [];
  });

  // Note: Content blocks (vision, mission, etc.) should be managed through the Features admin page
  // Offerings can also be managed through Features with appropriate categories
  const offerings: any[] = [];

  // Fetch team members
  const { data: teamMembers = [] } = useQuery('team', async () => {
    const response = await teamAPI.getTeam();
    return response.data?.results || response.data || [];
  });

  // All content now comes from database - no fallbacks

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "mainEntity": {
      "@type": "EducationalOrganization",
      "name": "Future Minds Academy",
      "description": "Future Minds Academy was founded by engineers and educators passionate about inspiring the next generation of innovators through hands-on STEM education.",
      "foundingDate": "2024",
      "founder": {
        "@type": "Person",
        "name": "Future Minds Academy Founders",
        "jobTitle": "Co-Founders",
        "description": "Engineers and educators dedicated to STEM education"
      }
    }
  };

  return (
    <div className="pt-28">
      <SEO 
        title="About Us | Our Mission & Story"
        description="Learn about Future Minds Academy's mission to provide hands-on STEM education. Founded by engineers and educators in Clayton, NC. Meet our team and discover our story."
        keywords="about Future Minds Academy, STEM education mission, Clayton NC educators, robotics teachers, coding instructors for kids"
        canonical="/about"
        structuredData={aboutSchema}
      />
      {/* Hero Section */}
      <section className="gradient-bg py-20">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              About {settings?.site_name || 'Future Minds Academy'}
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              {settings?.founder_tagline || 'Founded by Engineers. Inspired by Children. Building the Future Together.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="card p-8"
            >
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mb-6">
                <Target size={32} className="text-primary-500" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Our Vision</h2>
              <p className="text-gray-600 leading-relaxed">
                To empower the next generation of thinkers, creators, and problem-solvers by bridging the gap between childhood curiosity and the advanced technological landscape of the AI era.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="card p-8"
            >
              <div className="w-16 h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mb-6">
                <Rocket size={32} className="text-secondary-500" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
              <p className="text-gray-600 leading-relaxed">
                At Future Minds Academy, our mission is to provide high-quality, hands-on STEM education that inspires children to explore the worlds of robotics, coding, and artificial intelligence. Founded by engineering experts and educators, we prioritize interactive learning that nurtures young minds.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Offer (FIRST) */}
      {settings?.show_what_we_offer_section !== false && (
        <section className="section bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-display font-bold mb-4">What We Offer</h2>
              {settings?.program_schedule_text && (
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {settings.program_schedule_text}
                </p>
              )}
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {offerings.length > 0 && offerings.map((item: any, index: number) => {
                const iconMap: { [key: string]: React.ReactNode } = {
                  'BookOpen': <BookOpen size={32} />,
                  'Lightbulb': <Lightbulb size={32} />,
                  'Award': <Award size={32} />,
                  'GraduationCap': <GraduationCap size={32} />,
                  'Heart': <Heart size={32} />,
                  'Target': <Target size={32} />,
                  'Rocket': <Rocket size={32} />,
                  'Users': <Users size={32} />,
                };
                const itemIcon = typeof item.icon === 'string' ? (iconMap[item.icon] || <Award size={32} />) : item.icon;
                
                return (
                  <motion.div
                    key={item.id || index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center p-6"
                  >
                    <div className="w-16 h-16 bg-primary-500 rounded-2xl flex items-center justify-center mx-auto mb-6 text-white">
                      {itemIcon}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why Us Section (SECOND) */}
      {settings?.show_why_us_section !== false && (
      <section className="section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-display font-bold mb-4">Why Choose Us?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choosing the right STEM program is an investment in your child's future.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((item: any, index: number) => {
              const iconMap: { [key: string]: React.ReactNode } = {
                'GraduationCap': <GraduationCap size={40} />,
                'Lightbulb': <Lightbulb size={40} />,
                'Award': <Award size={40} />,
                'Heart': <Heart size={40} />,
                'Users': <Users size={40} />,
                'Target': <Target size={40} />,
                'Rocket': <Rocket size={40} />,
                'BookOpen': <BookOpen size={40} />,
              };
              const itemIcon = typeof item.icon === 'string' ? (iconMap[item.icon] || <Award size={40} />) : item.icon;
              const itemColor = item.color || 'primary';
              
              return (
                <motion.div
                  key={item.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card p-8 hover:-translate-y-2"
                >
                  <div className={`w-16 h-16 bg-${itemColor}-100 rounded-2xl flex items-center justify-center mb-6 text-${itemColor}-500`}>
                    {itemIcon}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Meet Our Team */}
      {settings?.show_team_section !== false && teamMembers.length > 0 && (
        <section className="section bg-gray-50">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
                Meet Our Team
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Dedicated educators and engineers passionate about inspiring the next generation.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {teamMembers.map((member: any, index: number) => (
                <motion.div
                  key={member.id || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden"
                >
                  <div className="aspect-square bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Users size={80} className="text-white/80" />
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                    <p className="text-primary-600 font-medium mb-3">{member.title}</p>
                    <p className="text-gray-600 text-sm line-clamp-3">{member.bio}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Our Story */}
      <section className="section bg-gray-900 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-display font-bold mb-6 text-center">Our Story</h2>
            {settings?.our_story ? (
              <div
                className="space-y-6 text-gray-300 leading-relaxed prose prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(settings.our_story.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>').replace(/^/, '<p>').replace(/$/, '</p>')) }}
              />
            ) : (
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p>Loading our story...</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
